import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  CircularProgress,
  useTheme,
  Button,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Sort as SortIcon,
  FilterList as FilterListIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  LocationOn as LocationOnIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useLocations } from '../hooks/useAnalytics';
import { glassStyle, animationVariants } from '../theme/luxuryTheme';
import { Location } from '../types';

type SortDirection = 'asc' | 'desc';
type SortField = 'name' | 'city' | 'region' | 'state';
type ViewMode = 'table' | 'grid';

const Locations: React.FC = () => {
  const theme = useTheme();
  const { data: locationsData, isLoading, error } = useLocations();
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [selectedRegion, setSelectedRegion] = useState<string>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const handleRegionFilter = (region: string) => {
    setSelectedRegion(region);
    handleFilterClose();
  };

  const handleViewModeChange = (_: React.MouseEvent<HTMLElement>, newMode: ViewMode | null) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  if (error || !locationsData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Typography variant="h6" color="error">
          Failed to load locations
        </Typography>
      </Box>
    );
  }

  // Filter locations by region
  const filteredLocations = selectedRegion === 'all'
    ? locationsData.locations
    : locationsData.locations.filter((location: Location) => location.region === selectedRegion);

  // Sort locations
  const sortedLocations = [...filteredLocations].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];

    if (sortDirection === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  const regions = ['all', ...Object.keys(locationsData.byRegion || {})];

  return (
    <Box>
      <motion.div {...animationVariants.fadeIn}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Locations
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Manage and view all store locations across regions
          </Typography>
        </Box>
      </motion.div>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.1 }}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Locations
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {locationsData.count}
              </Typography>
            </Card>
          </motion.div>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.2 }}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Active Stores
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {locationsData.locations.filter((loc: Location) => loc.isActive).length}
              </Typography>
            </Card>
          </motion.div>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.3 }}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Regions
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {Object.keys(locationsData.byRegion || {}).length}
              </Typography>
            </Card>
          </motion.div>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.4 }}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                States
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {new Set(locationsData.locations.map((loc: Location) => loc.state)).size}
              </Typography>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* Controls */}
      <motion.div {...animationVariants.fadeIn} transition={{ delay: 0.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={handleFilterClick}
              sx={{ borderRadius: 2 }}
            >
              {selectedRegion === 'all' ? 'All Regions' : selectedRegion}
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleFilterClose}
            >
              {regions.map(region => (
                <MenuItem
                  key={region}
                  onClick={() => handleRegionFilter(region)}
                  selected={region === selectedRegion}
                >
                  {region === 'all' ? 'All Regions' : region}
                  {region !== 'all' && (
                    <Chip
                      label={locationsData.byRegion[region]?.length || 0}
                      size="small"
                      sx={{ ml: 2 }}
                    />
                  )}
                </MenuItem>
              ))}
            </Menu>
          </Box>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
          >
            <ToggleButton value="table">
              <ViewListIcon />
            </ToggleButton>
            <ToggleButton value="grid">
              <ViewModuleIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </motion.div>

      {/* Table View */}
      {viewMode === 'table' ? (
        <motion.div {...animationVariants.fadeIn} transition={{ delay: 0.6 }}>
          <TableContainer component={Paper} sx={{ ...glassStyle }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'name'}
                      direction={sortField === 'name' ? sortDirection : 'asc'}
                      onClick={() => handleSort('name')}
                    >
                      Store Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'city'}
                      direction={sortField === 'city' ? sortDirection : 'asc'}
                      onClick={() => handleSort('city')}
                    >
                      City
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'state'}
                      direction={sortField === 'state' ? sortDirection : 'asc'}
                      onClick={() => handleSort('state')}
                    >
                      State
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'region'}
                      direction={sortField === 'region' ? sortDirection : 'asc'}
                      onClick={() => handleSort('region')}
                    >
                      Region
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedLocations.map((location) => (
                  <TableRow key={location.id} hover>
                    <TableCell>{location.name}</TableCell>
                    <TableCell>{location.city}</TableCell>
                    <TableCell>{location.state}</TableCell>
                    <TableCell>
                      <Chip
                        label={location.region}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(175, 35, 38, 0.2)',
                          color: theme.palette.primary.main,
                        }}
                      />
                    </TableCell>
                    <TableCell>{location.phone}</TableCell>
                    <TableCell>
                      <Chip
                        label={location.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={location.isActive ? 'success' : 'default'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </motion.div>
      ) : (
        // Grid View
        <Grid container spacing={3}>
          {sortedLocations.map((location, index) => (
            <Grid item xs={12} md={6} lg={4} key={location.id}>
              <motion.div
                {...animationVariants.scale}
                transition={{ delay: 0.1 * Math.min(index, 6) }}
              >
                <Card sx={{ ...glassStyle, p: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {location.name}
                    </Typography>
                    <Chip
                      label={location.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={location.isActive ? 'success' : 'default'}
                    />
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <LocationOnIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {location.address}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <BusinessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {location.city}, {location.state}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <PhoneIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {location.phone}
                    </Typography>
                  </Box>
                  
                  <Chip
                    label={location.region}
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(175, 35, 38, 0.2)',
                      color: theme.palette.primary.main,
                    }}
                  />
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default Locations;